import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, LockKeyhole } from "lucide-react";
import { useAppModal } from "../../components/AppModalProvider";
import {
  BIRTHPLACES,
  PROFESSIONS,
  RACES,
  defaultStory,
  type CreationInput,
} from "./catalog";

const CREATION_STEPS = ["身份", "职业", "据点", "档案"] as const;

interface Props {
  onCancel: () => void;
  onCreate: (input: CreationInput) => Promise<void>;
}

export function CreationWizard({ onCancel, onCreate }: Props) {
  const modal = useAppModal();
  const [step, setStep] = useState(1);
  const [gender, setGender] = useState<"male" | "female" | null>(null);
  const [raceId, setRaceId] = useState<"human" | "felyne" | "wyverian">(
    "human",
  );
  const [professionId, setProfessionId] =
    useState<CreationInput["professionId"]>("hunter");
  const [customProfession, setCustomProfession] = useState("");
  const [customHasHunterLicense, setCustomHasHunterLicense] = useState(false);
  const [birthplaceId, setBirthplaceId] = useState("kokoto");
  const defaults = useMemo(
    () =>
      defaultStory({ raceId, professionId, customProfession, birthplaceId }),
    [raceId, professionId, customProfession, birthplaceId],
  );
  const [background, setBackground] = useState<string | null>(null);
  const [hook, setHook] = useState<string | null>(null);
  const [characterName, setCharacterName] = useState("苍");
  const [archiveName, setArchiveName] = useState("新的旅程");
  const [saving, setSaving] = useState(false);
  const selectedRace = RACES.find((race) => race.id === raceId)!;
  const selectedProfession = PROFESSIONS.find(
    (profession) => profession.id === professionId,
  )!;
  const selectedBirthplace = BIRTHPLACES.find(
    (hub) => hub.id === birthplaceId,
  )!;
  const currentBackground = background ?? defaults.background;
  const currentHook = hook ?? defaults.hook;
  const canNext = step !== 1 || gender !== null;

  const previewBirthplace = async (hub: (typeof BIRTHPLACES)[number]) => {
    setBirthplaceId(hub.id);
    if (
      await modal.confirm({
        title: hub.label,
        message: hub.description,
        confirmLabel: "确认并继续",
        cancelLabel: "返回选择",
        visual: {
          imageUrl: hub.image,
          imageAlt: `${hub.label}场景预览`,
          eyebrow: hub.game,
          meta: "确认后将以这里作为故事的开局地点。",
        },
      })
    )
      setStep(4);
  };

  const submit = async () => {
    if (!gender || !characterName.trim() || !archiveName.trim()) return;
    setSaving(true);
    try {
      await onCreate({
        archiveName: archiveName.trim(),
        characterName: characterName.trim(),
        gender,
        raceId,
        professionId,
        customProfession,
        customHasHunterLicense,
        birthplaceId,
        background: currentBackground,
        hook: currentHook,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="creation-screen">
      <div className="creation-topbar">
        <button
          className="creation-back"
          aria-label={step === 1 ? "返回主菜单" : "上一步"}
          onClick={step === 1 ? onCancel : () => setStep(step - 1)}
        >
          <ArrowLeft size={15} />
          <span>{step === 1 ? "返回主菜单" : "上一步"}</span>
        </button>
        <div
          className="step-track"
          aria-label={`创建角色，第 ${step} 步，共 4 步`}
        >
          {CREATION_STEPS.map((label, index) => {
            const number = index + 1;
            return (
              <span
                className={
                  number < step ? "completed" : number === step ? "current" : ""
                }
                key={label}
              >
                <i>{number < step ? <Check size={11} /> : number}</i>
                <small>{label}</small>
              </span>
            );
          })}
        </div>
        <span className="creation-progress-label">角色登记 · {step} / 4</span>
      </div>

      {step === 1 && (
        <section className="wizard-page">
          <div className="wizard-heading">
            <span className="eyebrow">身份与种族</span>
            <h1>你将以何种身份踏入这个世界？</h1>
          </div>
          <div className="gender-row" role="group" aria-label="性别">
            <button
              className={gender === "male" ? "selected" : ""}
              onClick={() => setGender("male")}
            >
              男
            </button>
            <button
              className={gender === "female" ? "selected" : ""}
              onClick={() => setGender("female")}
            >
              女
            </button>
          </div>
          {!gender ? (
            <p className="selection-hint" aria-live="polite">
              请选择性别后继续。
            </p>
          ) : (
            <div className="race-grid">
              {RACES.map((race) => {
                const image =
                  race.id === "human" || race.id === "wyverian"
                    ? `${race.image}-${gender}-v2.webp`
                    : race.image;
                return (
                  <button
                    key={race.id}
                    disabled={race.locked}
                    className={`choice-card race-card ${race.id === raceId ? "selected" : ""}`}
                    style={{
                      backgroundImage: `linear-gradient(180deg, transparent 18%, rgba(7,10,8,.96) 82%), url(${image})`,
                    }}
                    onClick={() =>
                      !race.locked && setRaceId(race.id as typeof raceId)
                    }
                  >
                    {race.locked && (
                      <span className="locked">
                        <LockKeyhole size={14} /> 暂不可选
                      </span>
                    )}
                    <div className="choice-content">
                      <h2>{race.label}</h2>
                      <p>{race.description}</p>
                      {race.indices ? (
                        <div className="stat-grid">
                          {Object.entries({
                            血量: race.indices.health,
                            耐力: race.indices.stamina,
                            闪避率: race.indices.dodge,
                            攻击力: race.indices.attack,
                          }).map(([label, value]) => (
                            <span key={label}>
                              <small>{label}</small>
                              <b>{value}</b>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="unknown-stats">
                          待选择物种 · 数值未定
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {step === 2 && (
        <section className="wizard-page">
          <div className="wizard-heading">
            <span className="eyebrow">职业选择</span>
            <h1>你靠什么身份在据点立足？</h1>
            <p>
              只有持有猎人资格的角色能够使用任务板、行囊、工房与狩猎结算；其他职业仍可通过叙事参与世界。
            </p>
          </div>
          <div className="profession-grid">
            {PROFESSIONS.map((profession) => (
              <button
                key={profession.id}
                className={`choice-card compact ${professionId === profession.id ? "selected" : ""}`}
                onClick={() => setProfessionId(profession.id)}
              >
                <div>
                  <h2>{profession.label}</h2>
                  {profession.hasHunterLicense && (
                    <span className="license">持有猎人资格</span>
                  )}
                </div>
                <p>{profession.description}</p>
              </button>
            ))}
          </div>
          {professionId === "custom" && (
            <div className="custom-fields">
              <label>
                自定义职业
                <input
                  value={customProfession}
                  onChange={(event) => setCustomProfession(event.target.value)}
                  placeholder="例如：游历药师"
                />
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={customHasHunterLicense}
                  onChange={(event) =>
                    setCustomHasHunterLicense(event.target.checked)
                  }
                />
                持有猎人资格
              </label>
            </div>
          )}
        </section>
      )}

      {step === 3 && (
        <section className="wizard-page wide">
          <div className="wizard-heading">
            <span className="eyebrow">出生地</span>
            <h1>你的故事从哪座据点开始？</h1>
          </div>
          <div className="hub-grid">
            {BIRTHPLACES.map((hub) => (
              <button
                key={hub.id}
                className={`hub-card ${birthplaceId === hub.id ? "selected" : ""}`}
                style={{
                  backgroundImage: `linear-gradient(180deg, transparent 5%, rgba(8,11,9,.96) 92%), url(${hub.image})`,
                }}
                onClick={() => void previewBirthplace(hub)}
              >
                <span className="hub-game">{hub.game}</span>
                <div>
                  <h2>{hub.label}</h2>
                  <p>{hub.description}</p>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {step === 4 && (
        <section className="wizard-page final-step">
          <div className="wizard-heading">
            <span className="eyebrow">背景与开局钩子</span>
            <h1>写下旅程开始前的最后几笔</h1>
          </div>
          <div className="final-grid">
            <div>
              <label>
                角色名
                <input
                  value={characterName}
                  onChange={(event) => setCharacterName(event.target.value)}
                />
              </label>
              <label>
                存档名
                <input
                  value={archiveName}
                  onChange={(event) => setArchiveName(event.target.value)}
                />
              </label>
              <label>
                开局背景
                <textarea
                  rows={6}
                  value={currentBackground}
                  onChange={(event) => setBackground(event.target.value)}
                />
              </label>
              <label>
                开局钩子
                <textarea
                  rows={5}
                  value={currentHook}
                  onChange={(event) => setHook(event.target.value)}
                />
              </label>
            </div>
            <aside className="registration-preview">
              <span className="eyebrow">最终档案</span>
              <h2>{characterName || "未命名角色"}</h2>
              <dl>
                <dt>性别</dt>
                <dd>{gender === "male" ? "男" : "女"}</dd>
                <dt>种族</dt>
                <dd>{selectedRace.label}</dd>
                <dt>职业</dt>
                <dd>
                  {professionId === "custom"
                    ? customProfession || "自定义身份"
                    : selectedProfession.label}
                </dd>
                <dt>资格</dt>
                <dd>
                  {professionId === "custom"
                    ? customHasHunterLicense
                      ? "猎人资格"
                      : "无猎人资格"
                    : selectedProfession.hasHunterLicense
                      ? "猎人资格"
                      : "无猎人资格"}
                </dd>
                <dt>出生地</dt>
                <dd>{selectedBirthplace.label}</dd>
              </dl>
              <div className="mini-stats">
                血量 {selectedRace.indices?.health} · 耐力{" "}
                {selectedRace.indices?.stamina} · 闪避{" "}
                {selectedRace.indices?.dodge} · 攻击{" "}
                {selectedRace.indices?.attack}
              </div>
            </aside>
          </div>
        </section>
      )}

      {step !== 3 && (
        <div className="wizard-actions">
          {step < 4 ? (
            <button
              className="primary"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
            >
              下一步 <ArrowRight size={17} />
            </button>
          ) : (
            <button
              className="primary"
              disabled={saving || !characterName.trim() || !archiveName.trim()}
              onClick={() => void submit()}
            >
              <Check size={17} /> {saving ? "正在登记……" : "确认并开始旅程"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
